interface RaceChartProps {
  /** The chaser's running mark after each game played so far. */
  chaser: readonly number[];
  /** The record season's running mark after each of its games. */
  record: readonly number[];
  /** Games in the chaser's regular season, where the pace line ends. */
  seasonGames: number;
  /** Where the chaser's season finishes at this rate; null for a rate (points per game), which has no finish line. */
  pace: number | null;
  chaserLabel: string;
  recordLabel: string;
  /** Decimals for the end labels: 0 for counts, 1 for points per game. */
  decimals: number;
}

// The drawing's own units; the SVG scales to its container (capped in CSS so the labels stay small).
const WIDTH = 320;
const HEIGHT = 132;
const LEFT = 4;
const RIGHT = 286;
const TOP = 10;
const BOTTOM = 112;
/** End labels closer than this get pushed apart so they don't overlap. */
const LABEL_GAP = 11;

/**
 * A season chase as a race: the record season's running mark (grey), the
 * chaser's so far (gold), and a dotted line from where they are to where
 * their pace finishes. Each line ends in its number. Decorative beside the
 * card's text, which says the same thing, so it carries a short label.
 */
export function RaceChart({ chaser, record, seasonGames, pace, chaserLabel, recordLabel, decimals }: RaceChartProps) {
  const games = Math.max(seasonGames, record.length, chaser.length, 2);
  const values = [...chaser, ...record, ...(pace === null ? [] : [pace])];
  const isRate = pace === null;
  // Counts start from zero; a running average zooms in on its range, or every line would sit flat at the top.
  const low = isRate ? Math.min(...values) * 0.97 : 0;
  const high = Math.max(...values, low + 1) * (isRate ? 1.02 : 1);
  const x = (game: number) => LEFT + ((game - 1) / (games - 1)) * (RIGHT - LEFT);
  const y = (value: number) => BOTTOM - ((value - low) / (high - low)) * (BOTTOM - TOP);
  const line = (marks: readonly number[]) => marks.map((mark, i) => `${x(i + 1).toFixed(1)},${y(mark).toFixed(1)}`).join(" ");
  const format = (value: number) => value.toFixed(decimals);

  const last = chaser.length;
  const now = chaser.at(-1) ?? 0;
  const showPace = pace !== null && last < seasonGames;
  const recordEnd = record.at(-1) ?? 0;
  // The chaser's number sits at the end of their pace line, or where they are now.
  const chaserEnd = { x: showPace ? x(seasonGames) : x(last), y: y(showPace ? pace : now), value: showPace ? pace : now };
  let recordLabelY = y(recordEnd);
  let chaserLabelY = chaserEnd.y;
  if (Math.abs(recordLabelY - chaserLabelY) < LABEL_GAP) {
    const middle = (recordLabelY + chaserLabelY) / 2;
    const chaserAbove = chaserEnd.value >= recordEnd;
    chaserLabelY = middle + (chaserAbove ? -LABEL_GAP / 2 : LABEL_GAP / 2);
    recordLabelY = middle + (chaserAbove ? LABEL_GAP / 2 : -LABEL_GAP / 2);
  }

  return (
    <figure className="race">
      <svg
        className="race__chart"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`${chaserLabel}'s season so far against ${recordLabel}'s record season`}
      >
        <line className="race__axis" x1={LEFT} x2={RIGHT} y1={BOTTOM} y2={BOTTOM} />
        <polyline className="race__record" points={line(record)} />
        {showPace && <line className="race__pace" x1={x(last)} y1={y(now)} x2={x(seasonGames)} y2={y(pace)} />}
        <polyline className="race__chaser" points={line(chaser)} />
        <circle className="race__dot" cx={x(last)} cy={y(now)} r={3.5} />
        <text className="race__label" x={x(record.length) + 5} y={recordLabelY + 3.5}>
          {format(recordEnd)}
        </text>
        <text className="race__label race__label--chaser" x={chaserEnd.x + 5} y={chaserLabelY + 3.5}>
          {/* A pace is a projection, so it keeps its decimal: 11.7 wins, not a rounded-up 12. */}
          {showPace ? chaserEnd.value.toFixed(1) : format(chaserEnd.value)}
        </text>
        <text className="race__label" x={LEFT} y={HEIGHT - 4}>
          Game 1
        </text>
        <text className="race__label" x={RIGHT} y={HEIGHT - 4} textAnchor="end">
          Game {games}
        </text>
      </svg>
      <figcaption className="race__legend">
        <span className="race__key race__key--chaser">{chaserLabel}</span>
        <span className="race__key race__key--record">{recordLabel} (record)</span>
      </figcaption>
    </figure>
  );
}
